import {
  decorate,
  observable,
  action,
  runInAction,
  computed,
  reaction
} from "mobx";
import Hls from "hls.js";
import {
  DEFAULT_AUDIO_TRACK_NAME,
  LIVE_MEDIA_THRESHOLD_SECONDS,
  MEDIA_SOURCE
} from "../../utils/constants";

class HLSPlayerStore {
  constructor() {
    this.handleTimeUpdate = this.handleTimeUpdate.bind(this);
    this.handlePlay = this.handlePlay.bind(this);
    this.handlePause = this.handlePause.bind(this);
    this.handleVolumeChange = this.handleVolumeChange.bind(this);
    this.updateCurrentTime = this.updateCurrentTime.bind(this);
    this.handleLoadedMetadata = this.handleLoadedMetadata.bind(this);
    this.handleSliderTimeUpdate = this.handleSliderTimeUpdate.bind(this);
    this.toggleMute = this.toggleMute.bind(this);
    this.syncAudioToVideo = this.syncAudioToVideo.bind(this);
    this.handlePlayerWaiting = this.handlePlayerWaiting.bind(this);
    this.handlePlayerCanPlay = this.handlePlayerCanPlay.bind(this);
    this.handleVideoHlsManifestLoaded = this.handleVideoHlsManifestLoaded.bind(
      this
    );
    this.handleAudioHlsManifestLoaded = this.handleAudioHlsManifestLoaded.bind(
      this
    );
    this.handleHlsError = this.handleHlsError.bind(this);
    this.toggleCC = this.toggleCC.bind(this);

    this.reset();

    this.audioTrackReaction = reaction(
      () => this.selectedAudioTrack,
      selectedTrack => {
        if (
          this.audioHls &&
          this.audioHls.audioTracks &&
          this.audioHls.audioTracks.length > selectedTrack &&
          this.audioPlayerRef
        ) {
          this.audioHls.audioTrack = selectedTrack;
          this.hls.audioTrack = selectedTrack;
        }
      }
    );
  }

  reset() {
    this.clearVideoEventListeners();

    if (this.hls) {
      this.hls.destroy();
    }

    if (this.audioHls) {
      this.audioHls.destroy();
    }

    this.hls = null;
    this.audioHls = null;

    this.playerRef = null;
    this.audioPlayerRef = null;

    this.isPlaying = false;
    this.currentTime = 0;
    this.duration = 0;
    this.volume = 100;
    this.isMuted = false;
    this.ccEnabled = false;
    this.hasCCTrack = false;
    this.audioOffset = 0;
    this.isLiveFeed = false;
  }

  syncAudioToVideo() {
    if (!this.playerRef || !this.audioPlayerRef) {
      return;
    }

    const videoTime = this.playerRef.currentTime;
    const targetAudioTime = Math.max(0, videoTime + this.audioOffset);
    const currentAudioTime = this.audioPlayerRef.currentTime;

    const timeDifference = Math.abs(currentAudioTime - targetAudioTime);

    if (timeDifference > 0.3) {
      this.audioPlayerRef.currentTime = targetAudioTime;
    }
  }

  getCurrentTime() {
    return this.playerRef ? this.playerRef.currentTime : 0;
  }

  setCurrentTime(time) {
    if (this.playerRef) {
      this.playerRef.currentTime = time;
      this.currentTime = time;
      this.syncAudioToVideo();
    }
  }

  setAudioOffset(offsetTextValue) {
    const offsetValueFloat = parseFloat(offsetTextValue);

    if (!isNaN(offsetValueFloat) && this.audioOffset !== offsetValueFloat) {
      this.audioOffset = offsetValueFloat;
      this.syncAudioToVideo();
    } else if (offsetTextValue === "") {
      this.audioOffset = 0;
      this.syncAudioToVideo();
    }
  }

  _detectLiveFeed() {
    // Check if manifest indicates live stream
    if (this.hls.levels && this.hls.levels.length > 0) {
      const currentLevel = this.hls.levels[this.hls.currentLevel || 0];
      if (currentLevel && currentLevel.details) {
        return currentLevel.details.live === true;
      }
    }

    // Fallback: check if duration is Infinity (another indicator of live stream)
    return !!(this.playerRef && !isFinite(this.playerRef.duration));
  }

  handleVideoHlsManifestLoaded() {
    this.playerRef.pause();
    if (this.hls && this.hls.audioTracks) {
      // Create a mapping of the audio tracks on the manifest so we can filter out the tracks without worrying
      // about changing the selected track index if they are filtered out.
      this.audioTracks = this.hls.audioTracks.map((item, index) => {
        return {
          attrs: item.attrs,
          value: index
        };
      });
      if (
        this.selectedAudioTrack >= this.audioTracks.length ||
        this.selectedAudioTrack === undefined
      ) {
        this.selectedAudioTrack = 0;
      }
      this.hls.subtitleTrack = -1;
      this.hasCCTrack = this.hls.subtitleTracks.length > 0;
      this.isLiveFeed = this._detectLiveFeed();
    }
  }

  handleAudioHlsManifestLoaded() {
    this.audioPlayerRef.pause();

    if (
      this.audioHls &&
      this.audioHls.audioTracks &&
      this.audioHls.audioTracks.length > this.selectedAudioTrack
    ) {
      this.audioHls.audioTrack = this.selectedAudioTrack;
      this.audioPlayerRef.audioTrack = this.selectedAudioTrack;
    }
  }

  handleHlsError(event, data) {
    const errorType = data.type;
    const errorDetails = data.details;
    const errorFatal = data.fatal;
    console.log(
      `Received hls error ${errorType}, ${errorDetails}, ${errorFatal}`
    );
  }

  initializeHlsPlayer(playbackUrl, source, token, startInSeconds) {
    this.playerRef.pause();
    this.playerRef.volume = 0;

    // Check if the source for the content is DIAMOND or CEREBRO to determine if there should be
    // a bearer token supplied with the requests.
    this.hls = new Hls({
      xhrSetup: function(xhr, url) {
        if (
          source === MEDIA_SOURCE.DIAMOND ||
          source === MEDIA_SOURCE.CEREBRO
        ) {
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        }
      },
      autoStartLoad: true,
      startPosition: startInSeconds
    });

    this.audioHls = new Hls({
      xhrSetup: function(xhr, url) {
        if (
          source === MEDIA_SOURCE.DIAMOND ||
          source === MEDIA_SOURCE.CEREBRO
        ) {
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        }
      },
      autoStartLoad: true,
      startPosition: startInSeconds
    });

    if (playbackUrl) {
      this.audioHls.loadSource(playbackUrl);
      this.hls.loadSource(playbackUrl);
    }

    this.hls.attachMedia(this.playerRef);
    this.audioHls.attachMedia(this.audioPlayerRef);

    this.hls.on(Hls.Events.MANIFEST_LOADED, this.handleVideoHlsManifestLoaded);

    this.audioHls.on(
      Hls.Events.MANIFEST_LOADED,
      this.handleAudioHlsManifestLoaded
    );

    this.hls.on(Hls.Events.ERROR, this.handleHlsError);
    this.audioHls.on(Hls.Events.ERROR, this.handleHlsError);
  }

  updateHlsPlayer(prevSrc, newSrc, prevStart, newStart) {
    if (prevSrc !== newSrc) {
      this.hls.detachMedia();
      this.hls.loadSource(newSrc);
      this.hls.attachMedia(this.playerRef);
      // this won't actually load at startInSeconds (it will use startPosition from constructor)
      // but startLoad retriggering loading of the video
      this.hls.startLoad(newStart);

      this.audioHls.detachMedia();
      this.audioHls.loadSource(newSrc);
      this.audioHls.attachMedia(this.audioPlayerRef);
      this.audioHls.startLoad(newStart);
    }
  }

  setAudioTrackByIndex(index) {
    this.selectedAudioTrack = index;
  }

  get hlsAudioTrack() {
    if (
      this.audioHls.audioTracks &&
      this.audioHls.audioTracks.length > 0 &&
      this.audioHls.audioTrack !== -1
    ) {
      return this.audioHls.audioTracks[this.audioHls.audioTrack];
    } else {
      return undefined;
    }
  }

  get isSelectedDefaultAudioTrack() {
    if (
      this.selectedAudioTrack < 0 ||
      !this.audioTracks ||
      this.audioTracks.length === 0 ||
      this.selectedAudioTrack >= this.audioTracks.length
    ) {
      return false;
    }
    const attrs = this.audioTracks[this.selectedAudioTrack].attrs;
    const name = attrs && attrs.NAME;
    return name === DEFAULT_AUDIO_TRACK_NAME;
  }

  toggleIsPlaying() {
    if (this.isPlaying) {
      this.handlePause();
    } else {
      this.handlePlay();
    }
  }

  toggleMute() {
    const inverseValue = !this.isMuted;
    this.isMuted = inverseValue;
    this.audioPlayerRef.muted = inverseValue;
  }

  handleVolumeChange(volume) {
    this.volume = volume;
    this.audioPlayerRef.volume = volume / 100;

    const shouldMute = volume === 0;
    this.isMuted = shouldMute;
    this.audioPlayerRef.muted = shouldMute;
  }

  handleSliderTimeUpdate(time) {
    // Video player is source of truth
    this.playerRef.currentTime = time;
    this.currentTime = time;
    this.syncAudioToVideo();
  }

  updateCurrentTime(time) {
    if (Math.abs(time - (this.currentTime || 0)) > 0.1) {
      runInAction(() => {
        this.currentTime = time;
      });

      this.syncAudioToVideo();
    }
  }

  handleTimeUpdate() {
    if (!this.playerRef || !this.audioPlayerRef) {
      return;
    }
    this.updateCurrentTime(this.playerRef.currentTime);
  }

  handlePlay() {
    if (!this.playerRef || !this.audioPlayerRef) {
      return;
    }

    this.isPlaying = true;

    this.playerRef
      .play()
      .then(() => {
        this.syncAudioToVideo();

        // Start audio playback
        this.audioPlayerRef.play().catch(error => {
          console.warn("Audio play failed:", error);
        });
      })
      .catch(error => {
        console.warn("Video play failed:", error);
        this.isPlaying = false;
      });
  }

  handlePause() {
    if (!this.playerRef || !this.audioPlayerRef) {
      return;
    }
    this.isPlaying = false;

    // Pause both players
    this.playerRef.pause();
    this.audioPlayerRef.pause();
  }

  handleLoadedMetadata() {
    this.duration = this.playerRef.duration;

    // Initial sync when metadata is loaded
    if (this.audioPlayerRef) {
      this.volume = this.audioPlayerRef.volume * 100;
      this.syncAudioToVideo();
    }
  }

  handlePlayerWaiting() {
    if (this.audioPlayerRef && !this.audioPlayerRef.paused) {
      this.audioPlayerRef.pause();
    }
  }

  handlePlayerCanPlay() {
    if (this.isPlaying && this.audioPlayerRef && this.audioPlayerRef.paused) {
      this.syncAudioToVideo();
      this.audioPlayerRef.play().catch(error => {
        console.warn("Audio resume failed:", error);
      });
    }
  }

  setupVideoEventListeners() {
    if (!this.playerRef) {
      return;
    }
    this.playerRef.addEventListener(
      "loadedmetadata",
      this.handleLoadedMetadata
    );
    this.playerRef.addEventListener("timeupdate", this.handleTimeUpdate);
    this.playerRef.addEventListener("play", this.handlePlay);

    // Handle seeking to maintain sync
    this.playerRef.addEventListener("seeked", this.syncAudioToVideo);

    // Handle waiting/buffering states
    this.playerRef.addEventListener("waiting", this.handlePlayerWaiting);

    this.playerRef.addEventListener("canplay", this.handlePlayerCanPlay);
  }

  clearVideoEventListeners() {
    if (!this.playerRef) {
      return;
    }

    this.playerRef.removeEventListener(
      "loadedmetadata",
      this.handleLoadedMetadata
    );
    this.playerRef.removeEventListener("timeupdate", this.handleTimeUpdate);
    this.playerRef.removeEventListener("play", this.handlePlay);
    this.playerRef.removeEventListener("seeked", this.syncAudioToVideo);
    this.playerRef.removeEventListener("waiting", this.handlePlayerWaiting);
    this.playerRef.removeEventListener("canplay", this.handlePlayerCanPlay);
  }

  toggleCC() {
    if (this.hls) {
      const currentTrack = this.hls.subtitleTrack;
      this.hls.subtitleTrack = currentTrack === -1 ? 0 : -1; // Toggle between no track (-1) and the first track (0)
      this.ccEnabled = currentTrack === -1;
    }
  }

  get livePosition() {
    if (!this.isLiveFeed || !this.hls || !this.playerRef) {
      return null;
    }

    // Determine the most current position the Player could scrub to, in order of preference:
    // (1) This is the recommended way to get the live position in HLS.js
    // (2) Use seekable range end
    // (3) Jump to near the end of the buffer (duration - small buffer)
    if (
      this.hls.liveSyncPosition !== null &&
      this.hls.liveSyncPosition !== undefined
    ) {
      return this.hls.liveSyncPosition;
    } else if (this.playerRef.seekable && this.playerRef.seekable.length > 0) {
      return this.playerRef.seekable.end(this.playerRef.seekable.length - 1);
    } else if (this.playerRef.duration && isFinite(this.playerRef.duration)) {
      return Math.max(
        0,
        this.playerRef.duration - LIVE_MEDIA_THRESHOLD_SECONDS
      );
    }

    return null;
  }

  jumpToLive() {
    if (!this.isLiveFeed || !this.playerRef || !this.audioPlayerRef) {
      return;
    }

    const livePosition = this.livePosition;

    if (livePosition !== null) {
      this.playerRef.currentTime = livePosition;
      this.currentTime = livePosition;
      this.syncAudioToVideo();
    }
  }
}

decorate(HLSPlayerStore, {
  hls: observable,
  audioHls: observable,
  playerRef: observable,
  audioPlayerRef: observable,
  selectedAudioTrack: observable,
  audioTracks: observable,
  isPlaying: observable,
  currentTime: observable,
  duration: observable,
  isMuted: observable,
  volume: observable,
  setCurrentTime: action,
  initializeHlsPlayer: action,
  updateHlsPlayer: action,
  setAudioTrackByIndex: action,
  toggleIsPlaying: action,
  setupVideoEventListeners: action,
  clearVideoEventListeners: action,
  handleTimeUpdate: action,
  handlePlay: action,
  handlePause: action,
  handleVolumeChange: action,
  updateCurrentTime: action,
  handleSliderTimeUpdate: action,
  audioOffset: observable,
  setAudioOffset: action,
  syncAudioToVideo: action,
  hlsAudioTrack: computed,
  isSelectedDefaultAudioTrack: computed,
  toggleCC: action,
  ccEnabled: observable,
  hasCCTrack: observable,
  isLiveFeed: observable,
  livePosition: computed,
  jumpToLive: action
});

export default HLSPlayerStore;



// WEBPACK FOOTER //
// ./src/stores/customClips/HLSPlayerStore.js